import { useUnsavedChanges } from "../../layout/AppShellContext";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, apiCustom } from "../../api/client";
import { getTermRead, termReadErrorMessage } from "../../api/termRead";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import { useActiveCompanyIdApi } from "../../api/useActiveCompanyIdApi";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { formatCurrency } from "../shared/format";
import "../shared/mobileSection.scss";
import "./RecordsPage.scss";
import { ServiceClosures } from "./ServiceClosures";
import { BookingServicesNotice } from "../shared/BookingServicesNotice";
import { getRecordComment } from "./recordComment";

type CompanyInfo = {
    id: number;
    title?: string | null;
    address?: string | null;
};

type RecordsPermissions = {
    canView?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canManageMasterPermissions?: boolean;
    canCreate?: boolean;
    canViewClientPhone?: boolean;
};

type StaffOption = {
    id: number;
    name?: string | null;
};

type RecordServiceItem = {
    id: number;
    title?: string | null;
    amount?: number | null;
    amountDisplay?: string | null;
    length?: number | null;
    type?: string | null;
};

type RecordItem = {
    id: number;
    dateTime: string | null;
    master?: {
        id: number;
        name?: string | null;
    } | null;
    client?: {
        id: number;
        name?: string | null;
        phone?: string | null;
    } | null;
    services?: RecordServiceItem[];
    amount: number;
    amountFrom?: number | null;
    amountTo?: number | null;
    amountDisplay?: string | null;
    confirmed?: boolean;
    visitAttendance?: number;
    online?: boolean;
    comment?: string | null;
    commentRecord?: string | null;
    length?: number;
    seanceLength?: number;
    paidFull?: boolean;
    source?: {
        id: number;
        title?: string | null;
    } | null;
    company?: CompanyInfo | null;
    permissions?: {
        canEdit?: boolean;
        canDelete?: boolean;
    } | null;
    assignment?: {
        mode?: "any" | string;
        channel?: "widget" | "yandex" | string;
        assignmentStatus?: "waiting" | "assigned" | "released" | string;
        assignedAt?: string | null;
    } | null;
};

type MonthOption = {
    alias: string;
    apiDate: string;
};

type CalendarDay = {
    date: string;
    count: number;
    hasRecords: boolean;
};

type RecordsListResponse = {
    company?: CompanyInfo;
    permissions?: RecordsPermissions;
    items?: RecordItem[];
    limit?: number;
    activeDate?: string;
};

type PastCalendarResponse = {
    company?: CompanyInfo;
    permissions?: RecordsPermissions;
    activeMonth?: MonthOption;
    monthArrayName?: MonthOption[];
    days?: CalendarDay[];
};

type RecordDetailResponse = {
    success?: boolean;
    company?: CompanyInfo;
    permissions?: RecordsPermissions;
    item?: RecordItem;
    staffOptions?: StaffOption[];
};

type DetailFormState = {
    staffId: string;
    dateTime: string;
    comment: string;
};

type BookingPoolSettings = {
    enabled: boolean;
    manualEnabled?: boolean;
    autoEnabled?: boolean;
    autoEligible?: boolean;
    autoReason?: "no_future_staff_schedule" | null;
    scheduleCheckedFrom?: string;
    capacity: number;
};

type MasterPermissionItem = {
    staffId: number;
    name?: string | null;
    canDelete: boolean;
    canTransfer: boolean;
    configurable?: boolean;
    conflict?: boolean;
    reason?: string | null;
};

type MasterPermissionsResponse = {
    company?: CompanyInfo;
    items?: MasterPermissionItem[];
};

const ERROR_MESSAGES: Record<string, string> = {
    RECORD_LOCKED: "Запись уже связана с оплатой или бонусами. Перенос недоступен.",
    SLOT_UNAVAILABLE: "Выбранный слот уже занят.",
    STAFF_SCHEDULE_UNAVAILABLE: "У мастера нет рабочей смены на выбранное время.",
    STAFF_NOT_FOUND: "Выбранный мастер недоступен для текущей точки.",
    INVALID_DATE_TIME: "Укажите корректные дату и время.",
    POOL_SLOT_UNAVAILABLE: "Выбранный общий слот уже занят.",
    POOL_INVALID_INTERVAL: "Укажите корректный интервал записи.",
    POOL_RECORD_INVALID: "Общая запись повреждена. Обновите страницу или обратитесь в поддержку.",
    POOL_RECORD_UNAVAILABLE: "Общая запись уже недоступна для изменения.",
    POOL_RESERVATION_NOT_FOUND: "Резерв общего слота не найден. Обновите страницу.",
    POOL_ALREADY_ASSIGNED: "Запись уже назначена другому мастеру. Обновите страницу.",
    POOL_STAFF_INCOMPATIBLE: "Мастер не оказывает одну из выбранных услуг.",
    POOL_STAFF_BUSY: "У мастера уже есть запись на это время.",
    record_forbidden: "Нет права на это действие с записью.",
    record_not_found: "Запись не найдена. Обновите список.",
    MASTER_NOT_FOUND: "Мастер не найден в выбранной точке.",
};

const rubNumberFormatter = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 0,
});

const BOOKING_POOL_SETTINGS_VISIBLE = false;

function formatRecordAmount(item: Pick<RecordItem, "amount" | "amountFrom" | "amountTo">): string {
    const amountFrom = Number(item.amountFrom ?? NaN);
    const amountTo = Number(item.amountTo ?? NaN);

    if (Number.isFinite(amountFrom) && Number.isFinite(amountTo) && amountTo > amountFrom) {
        return `${rubNumberFormatter.format(amountFrom)} - ${rubNumberFormatter.format(amountTo)} ₽`;
    }

    return formatCurrency(item.amount);
}

function parseApiDateTime(value?: string | null): Date | null {
    if (!value) {
        return null;
    }

    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const date = new Date(normalized);

    return Number.isNaN(date.getTime()) ? null : date;
}

function parseLocalDate(value: string): Date | null {
    if (!value) {
        return null;
    }

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatRecordDateTime(value?: string | null): string {
    const date = parseApiDateTime(value);
    if (!date) {
        return "—";
    }

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

function formatCalendarDay(value: string): string {
    const date = parseLocalDate(value);
    if (!date) {
        return value;
    }

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        weekday: "short",
    }).format(date);
}

function toDateTimeLocalValue(value?: string | null): string {
    const date = parseApiDateTime(value);
    if (!date) {
        return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toApiDateTime(value: string): string {
    if (!value) {
        return "";
    }

    const normalized = value.slice(0, 16).replace("T", " ");
    return `${normalized}:00`;
}

function getServicesLabel(services?: RecordServiceItem[]): string {
    if (!services || services.length === 0) {
        return "Услуги не указаны";
    }

    return services
        .map((service) => service.title?.trim())
        .filter(Boolean)
        .join(", ");
}

function getErrorStatus(error: any): number | undefined {
    return Number.isFinite(Number(error?.status)) ? Number(error?.status) : undefined;
}

function getErrorMessage(error: any, fallback: string): string {
    const details = error?.details;
    const backendCode =
        (typeof details?.message === "string" && details.message) ||
        (typeof details?.error === "string" && details.error) ||
        null;

    if (backendCode && ERROR_MESSAGES[backendCode]) {
        return ERROR_MESSAGES[backendCode];
    }

    if (typeof details === "string" && details.trim()) {
        return details;
    }

    return error?.message ?? fallback;
}

function getMasterPermissionReason(item: MasterPermissionItem): string | null {
    if (item.reason === "company_franchisee") {
        return "Владелец или управляющий всегда имеет эти права.";
    }
    if (item.conflict) {
        return "Обнаружен конфликт настроек. Обратитесь к администратору.";
    }
    if (item.configurable === false) {
        return "У мастера не указан телефон — права нельзя настроить.";
    }

    return null;
}

function resolveInitialPastDate(days: CalendarDay[], preferredDate?: string | null): string {
    if (preferredDate && days.some((day) => day.date === preferredDate)) {
        return preferredDate;
    }

    const withRecords = days.filter((day) => day.hasRecords);
    if (withRecords.length > 0) {
        return withRecords[withRecords.length - 1].date;
    }

    return days[0]?.date ?? "";
}

export const RecordsPage: React.FC = () => {
    const location = useLocation();
    const detailRequest = useRef<AbortController | null>(null);
    const listRequest = useRef<AbortController | null>(null);
    const displayedCompanyId = useRef<string | null>(null);
    const { value: activeCompanyId, resolved: activeCompanyResolved } = useActiveCompanyId();
    const { value: activeCompanyIdApi, resolved: activeCompanyApiResolved } = useActiveCompanyIdApi();
    const [menuOpen, setMenuOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<"future" | "past">("future");
    const [company, setCompany] = useState<CompanyInfo | null>(null);
    const [permissions, setPermissions] = useState<RecordsPermissions | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [accessDeniedMessage, setAccessDeniedMessage] = useState("Нет доступа к записям по текущей точке.");

    const [futureLoading, setFutureLoading] = useState(false);
    const [futureError, setFutureError] = useState<string | null>(null);
    const [futureItems, setFutureItems] = useState<RecordItem[]>([]);
    const [futureFilter, setFutureFilter] = useState<"all" | "waiting">("all");
    const [poolSettings, setPoolSettings] = useState<BookingPoolSettings | null>(null);
    const [poolSettingsSaving, setPoolSettingsSaving] = useState(false);
    const [poolSettingsError, setPoolSettingsError] = useState<string | null>(null);
    const [masterPermissions, setMasterPermissions] = useState<MasterPermissionItem[]>([]);
    const [masterPermissionsLoading, setMasterPermissionsLoading] = useState(false);
    const [masterPermissionsError, setMasterPermissionsError] = useState<string | null>(null);
    const [masterPermissionSavingId, setMasterPermissionSavingId] = useState<number | null>(null);

    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState<string | null>(null);
    const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
    const [activeMonth, setActiveMonth] = useState<MonthOption | null>(null);
    const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

    const [pastLoading, setPastLoading] = useState(false);
    const [pastError, setPastError] = useState<string | null>(null);
    const [pastItems, setPastItems] = useState<RecordItem[]>([]);
    const [activeDate, setActiveDate] = useState("");

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [detailItem, setDetailItem] = useState<RecordItem | null>(null);
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [editMode, setEditMode] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState<DetailFormState>({
        staffId: "",
        dateTime: "",
        comment: "",
    });

    useUnsavedChanges(editMode || saving || deleting || poolSettingsSaving || masterPermissionSavingId !== null, "Редактирование записи");
    const applyMeta = (nextCompany?: CompanyInfo, nextPermissions?: RecordsPermissions) => {
        if (nextCompany) {
            setCompany(nextCompany);
        }

        if (nextPermissions) {
            setPermissions(nextPermissions);
        }
    };

    const handleForbidden = (message?: string) => {
        setAccessDenied(true);
        setAccessDeniedMessage(message || "Нет доступа к записям по текущей точке.");
    };

    const clearForbidden = () => {
        setAccessDenied(false);
        setAccessDeniedMessage("Нет доступа к записям по текущей точке.");
    };

    const beginListRequest = () => {
        listRequest.current?.abort();
        const controller = new AbortController();
        listRequest.current = controller;
        return controller;
    };

    const loadFuture = async () => {
        const controller = beginListRequest();
        try {
            setFutureLoading(true);
            setFutureError(null);

            const response = await getTermRead<RecordsListResponse>("/lkfr/records/future", { limit: 100 }, controller.signal);
            if (controller.signal.aborted) return;
            applyMeta(response?.company, response?.permissions);
            clearForbidden();
            setFutureItems(response?.items ?? []);
        } catch (error: any) {
            if (controller.signal.aborted) return;
            if (getErrorStatus(error) === 403) {
                setFutureItems([]);
                handleForbidden("Нет права на просмотр записей по выбранной точке.");
                return;
            }

            setFutureError(termReadErrorMessage(error, "Не удалось загрузить будущие записи"));
        } finally {
            if (!controller.signal.aborted) setFutureLoading(false);
        }
    };

    const loadPoolSettings = async () => {
        try {
            setPoolSettings(null);
            setPoolSettingsError(null);
            const response = await apiCustom.get<{settings?: BookingPoolSettings}>("/lkfr/records/pool/settings");
            setPoolSettings(response?.settings ?? null);
        } catch (error: any) {
            setPoolSettingsError(getErrorMessage(error, "Не удалось загрузить настройки записи к любому мастеру"));
        }
    };

    const savePoolSettings = async () => {
        if (!poolSettings) {
            return;
        }
        try {
            setPoolSettingsSaving(true);
            setPoolSettingsError(null);
            const response = await api.patch<{settings?: BookingPoolSettings}>("/api/gateway/records/pool/settings", {
                companyIdApi: activeCompanyIdApi ? Number(activeCompanyIdApi) : undefined,
                enabled: poolSettings.manualEnabled ?? poolSettings.enabled,
                capacity: poolSettings.capacity,
            });
            setPoolSettings(response?.settings ?? poolSettings);
        } catch (error: any) {
            setPoolSettingsError(getErrorMessage(error, "Не удалось сохранить настройки"));
        } finally {
            setPoolSettingsSaving(false);
        }
    };

    const loadMasterPermissions = async (preserveError = false) => {
        try {
            setMasterPermissionsLoading(true);
            if (!preserveError) {
                setMasterPermissionsError(null);
            }
            const response = await api.get<MasterPermissionsResponse>(
                "/api/gateway/records/master-permissions",
                {companyIdApi: Number(activeCompanyIdApi)}
            );
            applyMeta(response?.company);
            setMasterPermissions(response?.items ?? []);
        } catch (error: any) {
            setMasterPermissions([]);
            setMasterPermissionsError(getErrorMessage(error, "Не удалось загрузить права мастеров"));
        } finally {
            setMasterPermissionsLoading(false);
        }
    };

    const refreshMasterPermissionRow = async (staffId: number) => {
        try {
            const response = await api.get<MasterPermissionsResponse>(
                "/api/gateway/records/master-permissions",
                {companyIdApi: Number(activeCompanyIdApi)}
            );
            const refreshedItem = (response?.items ?? []).find((item) => item.staffId === staffId);
            if (refreshedItem) {
                setMasterPermissions((current) => current.map((master) =>
                    master.staffId === staffId ? refreshedItem : master
                ));
            }
        } catch {
            // Сохраняем откат и исходную ошибку PATCH; ручное обновление остаётся доступно.
        }
    };

    const updateMasterPermission = async (
        item: MasterPermissionItem,
        field: "canDelete" | "canTransfer",
        value: boolean
    ) => {
        if (
            masterPermissionSavingId !== null
            || item.configurable === false
            || permissions?.canManageMasterPermissions !== true
        ) {
            return;
        }

        const previousItem = item;
        setMasterPermissionsError(null);
        setMasterPermissionSavingId(item.staffId);
        setMasterPermissions((current) => current.map((master) =>
            master.staffId === item.staffId ? {...master, [field]: value} : master
        ));

        try {
            const response = await api.patch<{success?: boolean; item?: MasterPermissionItem}>(
                `/api/gateway/records/master-permissions/${item.staffId}`,
                {
                    companyIdApi: Number(activeCompanyIdApi),
                    [field]: value,
                }
            );
            if (!response?.item) {
                throw new Error("Сервер не вернул сохранённые права мастера");
            }
            const savedItem = response.item;
            setMasterPermissions((current) => current.map((master) =>
                master.staffId === item.staffId ? savedItem : master
            ));
        } catch (error: any) {
            setMasterPermissions((current) => current.map((master) =>
                master.staffId === previousItem.staffId ? previousItem : master
            ));
            setMasterPermissionsError(getErrorMessage(error, "Не удалось сохранить права мастера"));
            void refreshMasterPermissionRow(previousItem.staffId);
        } finally {
            setMasterPermissionSavingId(null);
        }
    };

    const loadPastList = async (date: string, activeRequest?: AbortController) => {
        const controller = activeRequest ?? beginListRequest();
        try {
            if (!activeRequest) setCalendarLoading(false);
            setPastLoading(true);
            setPastError(null);

            const response = await getTermRead<RecordsListResponse>("/lkfr/records/past", { date }, controller.signal);
            if (controller.signal.aborted) return;
            applyMeta(response?.company, response?.permissions);
            clearForbidden();
            setPastItems(response?.items ?? []);
            setActiveDate(response?.activeDate ?? date);
        } catch (error: any) {
            if (controller.signal.aborted) return;
            if (getErrorStatus(error) === 403) {
                setPastItems([]);
                handleForbidden("Нет права на просмотр записей по выбранной точке.");
                return;
            }

            setPastError(termReadErrorMessage(error, "Не удалось загрузить прошлые записи"));
        } finally {
            if (!controller.signal.aborted) setPastLoading(false);
        }
    };

    const loadPastCalendar = async (month?: string, preferredDate?: string) => {
        const controller = beginListRequest();
        try {
            setPastLoading(false);
            setCalendarLoading(true);
            setCalendarError(null);

            const response = await getTermRead<PastCalendarResponse>("/lkfr/records/past/calendar", month ? { month } : undefined, controller.signal);
            if (controller.signal.aborted) return;
            applyMeta(response?.company, response?.permissions);
            clearForbidden();

            const nextDays = response?.days ?? [];
            const nextActiveMonth = response?.activeMonth ?? null;
            const nextDate = resolveInitialPastDate(nextDays, preferredDate || activeDate);

            setMonthOptions(response?.monthArrayName ?? []);
            setActiveMonth(nextActiveMonth);
            setCalendarDays(nextDays);
            setActiveDate(nextDate);

            if (nextDate) {
                await loadPastList(nextDate, controller);
            } else {
                setPastItems([]);
            }
        } catch (error: any) {
            if (controller.signal.aborted) return;
            if (getErrorStatus(error) === 403) {
                setCalendarDays([]);
                setPastItems([]);
                handleForbidden("Нет права на просмотр записей по выбранной точке.");
                return;
            }

            setCalendarError(termReadErrorMessage(error, "Не удалось загрузить календарь записей"));
        } finally {
            if (!controller.signal.aborted) setCalendarLoading(false);
        }
    };

    const openDetail = async (recordId: number) => {
        detailRequest.current?.abort();
        const controller = new AbortController();
        detailRequest.current = controller;
        try {
            setDetailOpen(true);
            setDetailLoading(true);
            setDetailError(null);
            setSaveError(null);
            setSaveSuccess(null);
            setEditMode(false);

            const response = await apiCustom.get<RecordDetailResponse>(`/lkfr/records/${recordId}`, undefined, { signal: controller.signal });
            if (controller.signal.aborted) return;
            applyMeta(response?.company, response?.permissions);

            const nextItem = response?.item ?? null;
            setDetailItem(nextItem);
            setStaffOptions(response?.staffOptions ?? []);
            setForm({
                staffId: nextItem?.master?.id ? String(nextItem.master.id) : "",
                dateTime: toDateTimeLocalValue(nextItem?.dateTime),
                comment: getRecordComment(nextItem),
            });
        } catch (error: any) {
            if (!controller.signal.aborted) setDetailError(getErrorMessage(error, "Не удалось загрузить карточку записи"));
        } finally {
            if (!controller.signal.aborted) setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        detailRequest.current?.abort();
        setDetailOpen(false);
        setDetailLoading(false);
        setDetailError(null);
        setDetailItem(null);
        setStaffOptions([]);
        setEditMode(false);
        setSaveError(null);
        setSaveSuccess(null);
        setDeleting(false);
        setForm({
            staffId: "",
            dateTime: "",
            comment: "",
        });
    };

    const reloadActiveTab = async () => {
        if (activeTab === "future") {
            await loadFuture();
            return;
        }

        await loadPastCalendar(activeMonth?.apiDate, activeDate);
    };

    const handleSave = async () => {
        if (!detailItem) {
            return;
        }

        if (detailItem.assignment?.mode === "any" && !form.staffId) {
            setSaveError("Выберите мастера для назначения.");
            return;
        }

        try {
            setSaving(true);
            setSaveError(null);
            setSaveSuccess(null);

            const response = await api.patch<RecordDetailResponse>(`/api/gateway/records/${detailItem.id}`, {
                companyIdApi: activeCompanyIdApi ? Number(activeCompanyIdApi) : undefined,
                staffId: form.staffId ? Number(form.staffId) : undefined,
                dateTime: form.dateTime ? toApiDateTime(form.dateTime) : undefined,
                commentRecord: form.comment,
            });

            applyMeta(response?.company, response?.permissions);
            if (response?.item) {
                setDetailItem(response.item);
                setForm({
                    staffId: response.item.master?.id ? String(response.item.master.id) : "",
                    dateTime: toDateTimeLocalValue(response.item.dateTime),
                    comment: getRecordComment(response.item),
                });
            }

            if (response?.staffOptions) {
                setStaffOptions(response.staffOptions);
            }

            setSaveSuccess("Запись успешно обновлена");
            setEditMode(false);
            await reloadActiveTab();
        } catch (error: any) {
            setSaveError(getErrorMessage(error, "Не удалось сохранить запись"));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!detailItem || deleting) {
            return;
        }
        if (!window.confirm("Удалить запись? Это действие нельзя отменить.")) {
            return;
        }

        try {
            setDeleting(true);
            setSaveError(null);
            setSaveSuccess(null);
            const companyIdApi = activeCompanyIdApi ? Number(activeCompanyIdApi) : 0;
            await api.delete<{success?: boolean; id?: number}>(
                `/api/gateway/records/${detailItem.id}?companyIdApi=${encodeURIComponent(String(companyIdApi))}`
            );
            closeDetail();
            await reloadActiveTab();
        } catch (error: any) {
            setSaveError(getErrorMessage(error, "Не удалось удалить запись"));
            setDeleting(false);
        }
    };

    useEffect(() => {
        listRequest.current?.abort();
        setFutureLoading(false);
        setPastLoading(false);
        setCalendarLoading(false);
        if (!activeCompanyResolved || !activeCompanyApiResolved) {
            return;
        }

        const companyChanged = displayedCompanyId.current !== activeCompanyIdApi;
        if (companyChanged) {
            displayedCompanyId.current = activeCompanyIdApi;
            setCompany(null);
            setPermissions(null);
            setFutureItems([]);
            setPastItems([]);
            setCalendarDays([]);
            setMonthOptions([]);
            setActiveMonth(null);
            setActiveDate("");
            setFutureError(null);
            setPastError(null);
            setCalendarError(null);
            clearForbidden();
        }

        if (!activeCompanyId || !activeCompanyIdApi) {
            clearForbidden();
            setCompany(null);
            setPermissions(null);
            setFutureLoading(false);
            setFutureError(null);
            setFutureItems([]);
            setMasterPermissions([]);
            setMasterPermissionsLoading(false);
            setMasterPermissionsError(null);
            setMasterPermissionSavingId(null);
            setCalendarLoading(false);
            setCalendarError(null);
            setCalendarDays([]);
            setMonthOptions([]);
            setActiveMonth(null);
            setPastLoading(false);
            setPastError(null);
            setPastItems([]);
            setActiveDate("");
            closeDetail();
            return;
        }

        if (activeTab === "future") {
            void loadFuture();
            if (BOOKING_POOL_SETTINGS_VISIBLE) {
                void loadPoolSettings();
            }
            return () => listRequest.current?.abort();
        }

        void loadPastCalendar(
            companyChanged ? undefined : activeMonth?.apiDate || undefined,
            companyChanged ? undefined : activeDate || undefined
        );
        return () => listRequest.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeCompanyApiResolved, activeCompanyId, activeCompanyIdApi, activeCompanyResolved]);

    useEffect(() => {
        const permissionMatchesActiveCompany =
            Number(company?.id) === Number(activeCompanyIdApi)
            && permissions?.canManageMasterPermissions === true;
        if (
            !activeCompanyResolved
            || !activeCompanyApiResolved
            || !activeCompanyId
            || !activeCompanyIdApi
            || !permissionMatchesActiveCompany
        ) {
            setMasterPermissions([]);
            setMasterPermissionsError(null);
            return;
        }

        void loadMasterPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeCompanyApiResolved,
        activeCompanyId,
        activeCompanyIdApi,
        activeCompanyResolved,
        company?.id,
        permissions?.canManageMasterPermissions,
    ]);

    useEffect(() => {
        closeDetail();
        if (!activeCompanyResolved || !activeCompanyApiResolved || !activeCompanyId || !activeCompanyIdApi) return;
        const params = new URLSearchParams(location.search);
        const recordId = params.get("recordId") || "";
        if (params.get("from") === "operations-day" && params.get("operationCompanyId") === activeCompanyId && /^[1-9]\d*$/.test(recordId)) {
            void openDetail(Number(recordId));
        }
        return () => detailRequest.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, activeCompanyId, activeCompanyIdApi, activeCompanyResolved, activeCompanyApiResolved]);

    const canEditCurrentRecord =
        Boolean(permissions?.canEdit) &&
        Boolean(detailItem?.permissions?.canEdit);
    const canDeleteCurrentRecord =
        Boolean(permissions?.canDelete) &&
        Boolean(detailItem?.permissions?.canDelete);

    const currentLoading = activeTab === "future" ? futureLoading : pastLoading || calendarLoading;
    const currentError = activeTab === "future"
        ? futureError
        : (calendarError || pastError);
    const waitingItemsCount = futureItems.filter((item) =>
        item.assignment?.mode === "any" &&
        ["waiting", "released"].includes(item.assignment?.assignmentStatus || "")
    ).length;
    const displayedFutureItems = futureFilter === "waiting"
        ? futureItems.filter((item) =>
            item.assignment?.mode === "any" &&
            ["waiting", "released"].includes(item.assignment?.assignmentStatus || "")
        )
        : futureItems;
    const searchedFutureItems = displayedFutureItems.filter(item => `${item.client?.name || ""} ${item.master?.name || ""} ${getServicesLabel(item.services)} ${item.id}`.toLocaleLowerCase("ru-RU").includes(search.trim().toLocaleLowerCase("ru-RU")));

    return (
        <div className="section-page records-page">
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

            <div className="records-heading">
                <h1 className="section-title">Записи</h1>
                <Link className="business-secondary records-schedule-link" to="/schedule" aria-label="Расписание мастеров">Расписание</Link>
            </div>
            <label className="records-search">
                <span>Поиск в загруженных записях</span>
                <input
                    className="section-input"
                    type="search"
                    value={search}
                    placeholder="Клиент, мастер, услуга или №"
                    onChange={event => setSearch(event.target.value)}
                />
            </label>
            {new URLSearchParams(location.search).get("from") === "operations-day" && (
                <Link to="/operations-day" className="records-create-link">← Операционный день</Link>
            )}

            <div className="section-card records-context-card">
                <div className="section-subtitle">{company?.title || "Текущая точка"}</div>
                <div className="section-helper">{company?.address || "Адрес не указан"}</div>
                <div className="records-tabs">
                    <button
                        type="button"
                        className={`records-tab${activeTab === "future" ? " active" : ""}`}
                        onClick={() => setActiveTab("future")}
                    >
                        Будущие
                    </button>
                    <button
                        type="button"
                        className={`records-tab${activeTab === "past" ? " active" : ""}`}
                        onClick={() => setActiveTab("past")}
                    >
                        Прошлые
                    </button>
                </div>
                {permissions?.canCreate ? (
                    <Link to="/records/new" className="records-create-link">
                        Создать запись
                    </Link>
                ) : null}
            </div>

            {activeTab === "future" && !accessDenied ? <BookingServicesNotice companyId={activeCompanyId} refreshKey={futureItems} /> : null}

            {activeTab === "future" && activeCompanyIdApi && !accessDenied && permissions?.canManageMasterPermissions ? <ServiceClosures key={`service-closures-${activeCompanyIdApi}`} companyId={activeCompanyIdApi} /> : null}

            {BOOKING_POOL_SETTINGS_VISIBLE && permissions?.canEdit && poolSettings ? (
                <div className="section-card records-pool-settings">
                    <div>
                        <div className="section-subtitle">Запись к любому мастеру</div>
                        <div className="section-helper">
                            Общие слоты по графику филиала. Автоматически включаются, если с завтрашнего дня нет смен мастеров.
                        </div>
                    </div>
                    <label className="records-pool-toggle">
                        <input
                            type="checkbox"
                            checked={poolSettings.manualEnabled ?? poolSettings.enabled}
                            onChange={(event) => {
                                const manualEnabled = event.target.checked;
                                const autoEligible = poolSettings.autoEligible ?? false;
                                setPoolSettings({
                                    ...poolSettings,
                                    manualEnabled,
                                    autoEnabled: !manualEnabled && autoEligible,
                                    enabled: manualEnabled || autoEligible,
                                });
                            }}
                        />
                        <span>Включать вручную независимо от расписания</span>
                    </label>
                    <div className="section-helper">
                        {(poolSettings.manualEnabled ?? poolSettings.enabled)
                            ? "Сейчас общая запись включена вручную."
                            : poolSettings.autoEnabled
                                ? "Сейчас общая запись включена автоматически: с завтрашнего дня нет смен для онлайн-записи. Сегодняшний день не учитывается."
                                : "Сейчас общая запись выключена: условия автоматического включения не выполнены."}
                    </div>
                    <label className="section-field">
                        <span className="section-label">Одновременных записей</span>
                        <input
                            className="section-input"
                            type="number"
                            min={1}
                            max={50}
                            value={poolSettings.capacity}
                            onChange={(event) => setPoolSettings({
                                ...poolSettings,
                                capacity: Math.max(1, Math.min(50, Number(event.target.value) || 1)),
                            })}
                        />
                    </label>
                    {poolSettingsError ? <div className="section-error">{poolSettingsError}</div> : null}
                    <button
                        type="button"
                        className="section-btn"
                        disabled={poolSettingsSaving}
                        onClick={() => void savePoolSettings()}
                    >
                        {poolSettingsSaving ? "Сохраняем..." : "Сохранить настройки"}
                    </button>
                </div>
            ) : null}
            {BOOKING_POOL_SETTINGS_VISIBLE && permissions?.canEdit && !poolSettings && poolSettingsError ? (
                <div className="section-card section-error">{poolSettingsError}</div>
            ) : null}

            {!activeCompanyResolved || !activeCompanyApiResolved ? (
                <div className="section-empty">Подключаем активную точку...</div>
            ) : null}
            {activeCompanyResolved && activeCompanyApiResolved && !activeCompanyId ? (
                <div className="section-empty">Нет доступных точек для работы с записями.</div>
            ) : null}
            {activeCompanyResolved && activeCompanyApiResolved && activeCompanyId && !activeCompanyIdApi ? (
                <div className="section-empty">Для выбранной точки записи пока не подключены.</div>
            ) : null}
            {activeCompanyResolved && activeCompanyApiResolved && activeCompanyIdApi && accessDenied ? (
                <div className="section-error">{accessDeniedMessage}</div>
            ) : null}
            {activeCompanyResolved && activeCompanyApiResolved && activeCompanyIdApi && !accessDenied && currentError ? (
                <div className="section-error">
                    {currentError}
                    <button type="button" className="section-btn-secondary" disabled={currentLoading}
                        onClick={() => activeTab === "future"
                            ? void loadFuture()
                            : void loadPastCalendar(activeMonth?.apiDate || undefined, activeDate || undefined)}>
                        Повторить
                    </button>
                </div>
            ) : null}

            {activeTab === "future" && activeCompanyResolved && activeCompanyApiResolved && activeCompanyIdApi && !accessDenied ? (
                <>
                    <div className="section-card records-assignment-filter">
                        <button
                            type="button"
                            className={`records-filter-button${futureFilter === "all" ? " active" : ""}`}
                            onClick={() => setFutureFilter("all")}
                        >
                            Все записи
                        </button>
                        <button
                            type="button"
                            className={`records-filter-button${futureFilter === "waiting" ? " active" : ""}`}
                            onClick={() => setFutureFilter("waiting")}
                        >
                            Ждут назначения ({waitingItemsCount})
                        </button>
                    </div>
                    {futureLoading ? <div className="section-empty">Загрузка будущих записей...</div> : null}

                    {!futureLoading && !futureError && searchedFutureItems.length === 0 ? (
                        <div className="section-empty">
                            {futureFilter === "waiting" ? "Нет записей, ожидающих назначения." : "На ближайшее время записей не найдено."}
                        </div>
                    ) : null}

                    <div className="section-list">
                        {searchedFutureItems.map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                className="section-item records-record-button"
                                onClick={() => void openDetail(item.id)}
                            >
                                <div className="section-item-head">
                                    <div>
                                        <div className="section-item-title">{item.client?.name || "Клиент не указан"}</div>
                                        <div className="section-item-meta">{formatRecordDateTime(item.dateTime)}</div>
                                    </div>
                                    <div className="records-amount">{formatRecordAmount(item)}</div>
                                </div>
                                <div className="section-item-text">Мастер: {item.master?.name || "Не назначен"}</div>
                                <div className="section-item-text">Услуги: {getServicesLabel(item.services)}</div>
                                {item.client?.phone ? (
                                    <div className="section-item-text">Телефон: {item.client.phone}</div>
                                ) : null}
                                {getRecordComment(item) ? <div className="section-item-text">Комментарий: {getRecordComment(item)}</div> : null}
                                <div className="records-flags">
                                    {item.assignment?.mode === "any" && ["waiting", "released"].includes(item.assignment?.assignmentStatus || "") ? (
                                        <span className="section-badge warning">Ждёт назначения</span>
                                    ) : null}
                                    {item.assignment?.channel ? (
                                        <span className="section-badge">{item.assignment.channel === "yandex" ? "Яндекс" : "Виджет"}</span>
                                    ) : null}
                                    {item.online ? <span className="section-badge success">Онлайн</span> : null}
                                    {item.confirmed ? <span className="section-badge success">Подтверждена</span> : null}
                                    {item.visitAttendance ? <span className="section-badge warning">Был визит</span> : null}
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            ) : null}

            {activeTab === "past" && activeCompanyResolved && activeCompanyApiResolved && activeCompanyIdApi && !accessDenied ? (
                <>
                    <div className="section-card">
                        <div className="section-subtitle">Календарь</div>
                        <div className="section-field">
                            <div className="section-label">Месяц</div>
                            <select
                                className="section-select"
                                value={activeMonth?.apiDate || ""}
                                onChange={(event) => void loadPastCalendar(event.target.value, activeDate || undefined)}
                                disabled={calendarLoading}
                            >
                                {monthOptions.map((month) => (
                                    <option key={month.apiDate} value={month.apiDate}>
                                        {month.alias}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {calendarLoading && calendarDays.length === 0 ? (
                            <div className="section-empty">Загрузка календаря...</div>
                        ) : null}

                        {calendarDays.length > 0 ? (
                            <div className="records-calendar">
                                {calendarDays.map((day) => (
                                    <button
                                        type="button"
                                        key={day.date}
                                        className={`records-day${activeDate === day.date ? " active" : ""}${day.hasRecords ? " has-records" : ""}`}
                                        onClick={() => {
                                            setActiveDate(day.date);
                                            void loadPastList(day.date);
                                        }}
                                    >
                                        <span className="records-day-label">{formatCalendarDay(day.date)}</span>
                                        <span className="records-day-count">{day.count > 0 ? `${day.count} зап.` : "0"}</span>
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className="section-card">
                        <div className="section-subtitle">Список за день</div>
                        <div className="section-helper">
                            {activeDate ? `Дата: ${formatCalendarDay(activeDate)}` : "Выберите день в календаре"}
                        </div>
                    </div>

                    {currentLoading && activeDate ? <div className="section-empty">Загрузка списка за день...</div> : null}

                    {!currentLoading && !currentError && activeDate && pastItems.length === 0 ? (
                        <div className="section-empty">За выбранный день прошлых записей нет.</div>
                    ) : null}

                    <div className="section-list">
                        {pastItems.filter(item => `${item.client?.name || ""} ${item.master?.name || ""} ${getServicesLabel(item.services)} ${item.id}`.toLocaleLowerCase("ru-RU").includes(search.trim().toLocaleLowerCase("ru-RU"))).map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                className="section-item records-record-button"
                                onClick={() => void openDetail(item.id)}
                            >
                                <div className="section-item-head">
                                    <div>
                                        <div className="section-item-title">{item.client?.name || "Клиент не указан"}</div>
                                        <div className="section-item-meta">{formatRecordDateTime(item.dateTime)}</div>
                                    </div>
                                    <div className="records-amount">{formatRecordAmount(item)}</div>
                                </div>
                                <div className="section-item-text">Мастер: {item.master?.name || "Не назначен"}</div>
                                <div className="section-item-text">Услуги: {getServicesLabel(item.services)}</div>
                                {item.client?.phone ? (
                                    <div className="section-item-text">Телефон: {item.client.phone}</div>
                                ) : null}
                                {getRecordComment(item) ? <div className="section-item-text">Комментарий: {getRecordComment(item)}</div> : null}
                            </button>
                        ))}
                    </div>
                </>
            ) : null}

            {activeCompanyResolved
            && activeCompanyApiResolved
            && activeCompanyId
            && activeCompanyIdApi
            && Number(company?.id) === Number(activeCompanyIdApi)
            && permissions?.canManageMasterPermissions === true ? (
                <details className="section-card records-master-permissions" key={activeCompanyIdApi}>
                    <summary className="records-permissions-summary">
                        <span>
                            <strong>Права мастеров</strong>
                            <small>Удаление и перенос записей</small>
                        </span>
                        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </summary>
                    <div className="records-master-permissions-body">
                        <div className="records-master-permissions-head">
                            <div>
                                <div className="section-helper">
                                    Разрешения действуют для онлайн- и ручных записей. Ваши права франчайзи не ограничиваются.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="section-btn-secondary records-master-permissions-refresh"
                                disabled={masterPermissionsLoading || masterPermissionSavingId !== null}
                                onClick={() => void loadMasterPermissions()}
                            >
                                Обновить
                            </button>
                        </div>

                        {masterPermissionsError ? (
                            <div className="section-error">{masterPermissionsError}</div>
                        ) : null}
                        {masterPermissionsLoading && masterPermissions.length === 0 ? (
                            <div className="section-empty records-master-permissions-empty">Загрузка прав мастеров...</div>
                        ) : null}
                        {!masterPermissionsLoading && !masterPermissionsError && masterPermissions.length === 0 ? (
                            <div className="section-empty records-master-permissions-empty">У выбранной точки нет мастеров.</div>
                        ) : null}

                        {masterPermissions.length > 0 ? (
                            <div className="records-master-permissions-list">
                                {masterPermissions.map((master) => {
                                    const savingMaster = masterPermissionSavingId === master.staffId;
                                    const disabledMaster = master.configurable === false;
                                    const reason = getMasterPermissionReason(master);
                                    return (
                                        <div className="records-master-permission-card" key={master.staffId}>
                                            <div className="records-master-permission-name">
                                                {master.name || `Мастер #${master.staffId}`}
                                                {savingMaster ? <span>Сохраняем...</span> : null}
                                            </div>
                                            {reason ? (
                                                <div className={`records-master-permission-reason${master.conflict ? " conflict" : ""}`}>
                                                    {reason}
                                                </div>
                                            ) : null}
                                            <label className="records-master-permission-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={master.canDelete === true}
                                                    disabled={disabledMaster || masterPermissionSavingId !== null}
                                                    onChange={(event) => void updateMasterPermission(master, "canDelete", event.target.checked)}
                                                />
                                                <span>
                                                    <strong>Удаление записей</strong>
                                                    <small>Онлайн и ручные записи</small>
                                                </span>
                                            </label>
                                            <label className="records-master-permission-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={master.canTransfer === true}
                                                    disabled={disabledMaster || masterPermissionSavingId !== null}
                                                    onChange={(event) => void updateMasterPermission(master, "canTransfer", event.target.checked)}
                                                />
                                                <span>
                                                    <strong>Перенос записей</strong>
                                                    <small>На другую дату и время</small>
                                                </span>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </details>
            ) : null}

            {detailOpen ? (
                <div className="records-modal-backdrop" onClick={() => {
                    if (!deleting) {
                        closeDetail();
                    }
                }}>
                    <div className="records-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="records-modal-head">
                            <div>
                                <div className="section-subtitle">Карточка записи</div>
                                <div className="section-helper">ID #{detailItem?.id ?? "—"}</div>
                            </div>
                            <button type="button" className="records-close" disabled={deleting} onClick={closeDetail}>
                                ×
                            </button>
                        </div>

                        {detailLoading ? <div className="section-empty">Загрузка записи...</div> : null}
                        {detailError ? <div className="section-error">{detailError}</div> : null}
                        {saveError ? <div className="section-error">{saveError}</div> : null}
                        {saveSuccess ? <div className="section-success">{saveSuccess}</div> : null}

                        {!detailLoading && detailItem ? (
                            <>
                                <div className="section-card section-card-nested">
                                    <div className="section-item-text">
                                        Точка: {detailItem.company?.title || company?.title || "Текущая точка"}
                                    </div>
                                    <div className="section-item-text">
                                        Адрес: {detailItem.company?.address || company?.address || "Адрес не указан"}
                                    </div>
                                    <div className="section-item-text">
                                        Клиент: {detailItem.client?.name || "Клиент не указан"}
                                    </div>
                                    {detailItem.client?.phone ? (
                                        <div className="section-item-text">Телефон: {detailItem.client.phone}</div>
                                    ) : null}
                                    <div className="section-item-text">
                                        Услуги: {getServicesLabel(detailItem.services)}
                                    </div>
                                    <div className="section-item-text">
                                        Сумма: {formatRecordAmount(detailItem)}
                                    </div>
                                    <div className="section-item-text">
                                        Источник: {detailItem.source?.title || "Не указан"}
                                    </div>
                                    {detailItem.assignment?.mode === "any" ? (
                                        <div className="section-item-text">
                                            Назначение: {detailItem.assignment.assignmentStatus === "assigned" ? "мастер назначен" : "ожидает мастера"}
                                            {detailItem.assignment.channel ? ` · ${detailItem.assignment.channel === "yandex" ? "Яндекс" : "Виджет"}` : ""}
                                        </div>
                                    ) : null}
                                </div>

                                {!editMode ? (
                                    <div className="section-card section-card-nested">
                                        <div className="section-item-text">
                                            Мастер: {detailItem.master?.name || "Не назначен"}
                                        </div>
                                        <div className="section-item-text">
                                            Дата и время: {formatRecordDateTime(detailItem.dateTime)}
                                        </div>
                                        <div className="section-item-text">
                                            Комментарий: {getRecordComment(detailItem) || "—"}
                                        </div>
                                        <div className="section-item-text">
                                            Подтверждение: {detailItem.confirmed ? "Да" : "Нет"}
                                        </div>
                                        <div className="section-item-text">
                                            Онлайн: {detailItem.online ? "Да" : "Нет"}
                                        </div>
                                        <div className="section-actions">
                                            {canEditCurrentRecord ? (
                                                <button
                                                    type="button"
                                                    className="section-btn"
                                                    disabled={deleting}
                                                    onClick={() => setEditMode(true)}
                                                >
                                                    {detailItem.assignment?.mode === "any" && !detailItem.master ? "Назначить мастера" : "Редактировать"}
                                                </button>
                                            ) : null}
                                            {canDeleteCurrentRecord ? (
                                                <button
                                                    type="button"
                                                    className="section-btn-secondary records-delete-button"
                                                    disabled={deleting}
                                                    onClick={() => void handleDelete()}
                                                >
                                                    {deleting ? "Удаляем..." : "Удалить запись"}
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="section-btn-secondary"
                                                disabled={deleting}
                                                onClick={closeDetail}
                                            >
                                                Закрыть
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="section-card section-card-nested">
                                        <div className="section-form">
                                            <div className="section-field">
                                                <div className="section-label">Мастер</div>
                                                <select
                                                    className="section-select"
                                                    value={form.staffId}
                                                    onChange={(event) => setForm((current) => ({
                                                        ...current,
                                                        staffId: event.target.value,
                                                    }))}
                                                >
                                                    <option value="">Выберите мастера</option>
                                                    {staffOptions.map((staff) => (
                                                        <option key={staff.id} value={staff.id}>
                                                            {staff.name || `Мастер #${staff.id}`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="section-field">
                                                <div className="section-label">Дата и время</div>
                                                <input
                                                    className="section-input"
                                                    type="datetime-local"
                                                    value={form.dateTime}
                                                    onChange={(event) => setForm((current) => ({
                                                        ...current,
                                                        dateTime: event.target.value,
                                                    }))}
                                                />
                                            </div>

                                            <div className="section-field">
                                                <div className="section-label">Комментарий</div>
                                                <textarea
                                                    className="section-textarea"
                                                    value={form.comment}
                                                    onChange={(event) => setForm((current) => ({
                                                        ...current,
                                                        comment: event.target.value,
                                                    }))}
                                                    placeholder="Комментарий к записи"
                                                />
                                            </div>
                                        </div>

                                        <div className="section-actions">
                                            <button
                                                type="button"
                                                className="section-btn"
                                                disabled={saving}
                                                onClick={() => void handleSave()}
                                            >
                                                {saving ? "Сохраняем..." : "Сохранить"}
                                            </button>
                                            <button
                                                type="button"
                                                className="section-btn-secondary"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditMode(false);
                                                    setSaveError(null);
                                                    setSaveSuccess(null);
                                                    setForm({
                                                        staffId: detailItem.master?.id ? String(detailItem.master.id) : "",
                                                        dateTime: toDateTimeLocalValue(detailItem.dateTime),
                                                        comment: getRecordComment(detailItem),
                                                    });
                                                }}
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default RecordsPage;
